import React, { useState, useMemo, useCallback } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Drawer, Button, Form, message } from "antd";
import { connect } from "dva";
import { GatePassStatus, Drivability, MediaPurpose, VehicleColor } from "@/utils/enum";
import { hideJiraWidget, showJiraWidget, getUrlParams, parseAsNumber } from "@/utils/utils";
import { updateDocument } from "@/services/media";
import useScreenDetector from "@/hooks/useScreenDetector";
import DrawerWrapper from "@/components/DrawerWrapper";
import VehicleForm from "@/forms/shared/Vehicle/VehicleForm";

const defaultVehicleValues = {
  inoperational: Drivability.RideAndDrive,
  gatePassStatus: GatePassStatus.NotRequired,
};

const AddVehicleForm = ({ isAdmin, dispatch, adminShipmentDetails, shipmentDetails, configPerm, refreshData }) => {
  const { isMobile } = useScreenDetector(1100);
  const [visible, setVisible] = useState(false);
  const [loadingAddVehicle, setLoadingAddVehicle] = useState(false);

  const shipmentData = useMemo(
    () => (isAdmin ? adminShipmentDetails : shipmentDetails),
    [isAdmin, adminShipmentDetails, shipmentDetails],
  );

  const dispatchTypeAddVehicle = useMemo(
    () => (isAdmin ? "adminShipmentView/addVehicleShipment" : "shipmentDetails/addVehicleShipment"),
    [isAdmin],
  );

  const [vehicleFormRef] = Form.useForm();

  const showDrawer = useCallback(() => {
    setVisible(true);
    hideJiraWidget();
  }, []);

  const onClose = useCallback(() => {
    showJiraWidget();
    vehicleFormRef.resetFields();
    setVisible(false);
  }, [vehicleFormRef]);

  const onSubmit = useCallback(
    async result => {
      const shipmentId = getUrlParams("shipmentId");

      setLoadingAddVehicle(true);
      let gatePassObj = { url: null };

      if (result.gatePassStatus === GatePassStatus.Attached) {
        if (!result.gatePassFile.length === 0) {
          return;
        }

        try {
          gatePassObj = await updateDocument(result.gatePassFile[0].originFileObj, MediaPurpose.GatePass);
        } catch (error) {
          message.error("Something went wrong when trying to upload the gate pass, please try again later.");
          return;
        }
      }

      const usingCustomVehicleColor = result?.color === VehicleColor.Other;

      const vehiclePayload = {
        dealerId: shipmentData.data.dealerId,
        vin: result.vin?.toUpperCase(),
        year: result.year,
        make: result.make,
        model: result.model,
        vehicleType: result.vehicleType,
        trimPackage: result.trimPackageName,
        drivabilityType: result.inoperational,
        stockNumber: result.stockNumber,
        curbWeight: result.curbWeight,
        bodySubType: result.bodySubType,
        width: parseAsNumber(result?.width, 0),
        height: parseAsNumber(result?.height, 0),
        length: parseAsNumber(result?.length, 0),
        exteriorDimensions: result.exteriorDimensions,
        color: usingCustomVehicleColor ? result?.customColor : result?.color,
        otherType: result.vehicleTypeComment,
        comments: result.comments,
        truckWithDualWheels: result.truckWithDualWheels,
        truckWithLongWheelBase: result.truckWithLongWheelBase,
        isEV: result.isEV,
        shipmentId,
        gatePassType: result.gatePassStatus,
        gatePassUrl: gatePassObj.url,
        msrp: result.msrp,
        dealerPrice: result.dealerPrice,
        transporterPrice: result.transporterPrice,
        dealerPriceFeedbackType: result.dealerPriceFeedbackType,
        transporterPriceFeedbackType: result.transporterPriceFeedbackType,
        dealerOtherFeedbackNote: result.dealerOtherFeedbackNote,
        transporterOtherFeedbackNote: result.transporterOtherFeedbackNote,
      };

      dispatch({
        type: dispatchTypeAddVehicle,
        vehiclePayload,
      })
        .then(res => {
          if (res && !res.error) {
            onClose();
            refreshData?.();
          }
        })
        .finally(() => {
          setLoadingAddVehicle(false);
        });
    },
    [shipmentData?.data?.dealerId, dispatchTypeAddVehicle, onClose, dispatch, refreshData],
  );

  return (
    <>
      <Button type="primary" onClick={showDrawer} icon={<PlusOutlined />}>
        {!isMobile && "Add Vehicles"}
      </Button>
      <DrawerWrapper visible={visible}>
        <Drawer
          key={visible ? "open" : "closed"}
          title="Add vehicle to Shipment"
          width={isMobile ? "80%" : "40%"}
          onClose={onClose}
          visible={visible}
          bodyStyle={{ paddingBottom: 80 }}
          footer={
            <div style={{ textAlign: "right" }}>
              <Button onClick={onClose} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button onClick={vehicleFormRef.submit} type="primary" htmlType="submit" loading={loadingAddVehicle}>
                Add
              </Button>
            </div>
          }
        >
          <VehicleForm
            defaultValues={defaultVehicleValues}
            configPerm={configPerm}
            formRef={vehicleFormRef}
            onSubmit={onSubmit}
            shipmentData={shipmentData?.data}
          />
        </Drawer>
      </DrawerWrapper>
    </>
  );
};

export default connect(({ adminShipmentView, shipmentDetails, configs }) => ({
  configPerm: configs.configPerm,
  adminShipmentDetails: adminShipmentView,
  shipmentDetails,
}))(AddVehicleForm);
